'use client'
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"

import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/hooks/use-auth'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { parseCreditsToMicroUsd } from '@/lib/billing/utils/credits'
import { Loader2 } from 'lucide-react'
import { useTranslations } from "next-intl"



interface WalletBalance {
  balanceMicrousd: number
  isLow: boolean
  isDepleted: boolean
}

interface Transaction {
  id: string
  amount_microusd: number
  operation_type: string
  reference_type: string | null
  description: string | null
  created_at: string
  /** Set on grants given an expiry. */
  expires_at: string | null
}

function opTypeBadge(op: string) {
  if (op.startsWith('usage_')) return <Badge variant="outline">{op}</Badge>
  if (op.includes('grant') || op === 'topup') return <Badge variant="default">{op}</Badge>
  if (op === 'manual_deduct') return <Badge variant="destructive">{op}</Badge>
  return <Badge variant="secondary">{op}</Badge>
}

interface TenantWalletDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenantId: string
  tenantName: string
  canGrant: boolean
  onChanged: () => void
}

export function TenantWalletDialog({
  open, onOpenChange, tenantId, tenantName, canGrant, onChanged,
}: TenantWalletDialogProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()

  const { formatCredits, formatCreditsSigned } = useCreditFormatters()


  const tUi = useTranslations("Billing.BillingTenantWalletDialog")

  const { fetchWithAuth } = useAuth()
  const [balance, setBalance] = useState<WalletBalance | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Action form
  const [action, setAction] = useState<'grant' | 'deduct'>('grant')
  const [amountCredits, setAmountCredits] = useState('')
  const [description, setDescription] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const fetchWallet = useCallback(async () => {
    try {
      setLoading(true)
      setLoadError(null)
      const res = await fetchWithAuth(`/api/admin/billing/tenants/${tenantId}/wallet`)
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error || `Failed to fetch wallet (${res.status})`)
      }
      const data = await res.json()
      setBalance(data.balance)
      setTransactions(data.transactions ?? [])
    } catch (err) {
      setBalance(null)
      setTransactions([])
      setLoadError(err instanceof Error ? err.message : 'Failed to load wallet')
    } finally {
      setLoading(false)
    }
  }, [fetchWithAuth, tenantId])

  useEffect(() => {
    if (open) {
      fetchWallet()
      setAmountCredits('')
      setDescription('')
      setExpiresAt('')
      setActionError(null)
    }
  }, [open, fetchWallet])

  const handleSubmit = async () => {
    const amountMicro = parseCreditsToMicroUsd(amountCredits)
    if (amountMicro === null || amountMicro <= 0) {
      setActionError('Enter a valid positive amount')
      return
    }

    try {
      setSubmitting(true)
      setActionError(null)
      const res = await fetchWithAuth(`/api/admin/billing/tenants/${tenantId}/wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          amount_microusd: amountMicro,
          description: description.trim() || undefined,
          // `datetime-local` is the operator's local time; the API takes an absolute instant.
          expires_at: action === 'grant' && expiresAt ? new Date(expiresAt).toISOString() : undefined,
        }),
      })
      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Operation failed')
      }
      setAmountCredits('')
      setDescription('')
      setExpiresAt('')
      fetchWallet()
      onChanged()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Operation failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span><span key={tUi("wallet")}>{tUi("wallet")}</span></span> {tenantName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : loadError ? (
          <div className="text-center text-destructive py-8">
            <p className="text-sm">{loadError}</p>
          </div>
        ) : (
          <div className="space-y-6 py-2">
            {/* Balance */}
            {balance && (
              <div className="flex items-center gap-4">
                <div className="text-2xl font-bold font-mono break-words">
                  <span className={balance.isDepleted ? 'text-destructive' : balance.isLow ? 'text-yellow-600' : ''}>
                    {formatCredits(balance.balanceMicrousd)}
                  </span>
                </div>
                {balance.isDepleted && <Badge variant="destructive"><span key={tUi("depleted")}>{tUi("depleted")}</span></Badge>}
                {balance.isLow && !balance.isDepleted && <Badge variant="secondary"><span key={tUi("low")}>{tUi("low")}</span></Badge>}
              </div>
            )}

            {/* Manual Operation */}
            {canGrant && (
              <div className="space-y-3 border rounded-md p-3">
                <Label className="text-sm font-semibold"><span key={tUi("manualOperation")}>{tUi("manualOperation")}</span></Label>
                <div className="grid grid-cols-[120px_1fr_auto] gap-2 items-end">
                  <div>
                    <Select value={action} onValueChange={(v) => setAction(v as 'grant' | 'deduct')}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="grant"><span key={tUi("grant")}>{tUi("grant")}</span></SelectItem>
                        <SelectItem value="deduct"><span key={tUi("deduct")}>{tUi("deduct")}</span></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder={tUi("amountCredits")}
                      value={amountCredits}
                      onChange={(e) => setAmountCredits(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleSubmit} disabled={submitting} size="sm">
                    {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    <span><span key={tUi("apply")}>{tUi("apply")}</span></span>
                  </Button>
                </div>
                <Input
                  placeholder={tUi("descriptionOptional")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                {action === 'grant' && (
                  <div className="space-y-1">
                    <Label htmlFor="wallet-grant-expires" className="text-xs text-muted-foreground">
                      <span key={tUi("expiresOptionalEmptyNever")}>{tUi("expiresOptionalEmptyNever")}</span></Label>
                    <Input
                      id="wallet-grant-expires"
                      type="datetime-local"
                      value={expiresAt}
                      onChange={(e) => setExpiresAt(e.target.value)}
                    />
                  </div>
                )}
                {actionError && <p className="text-sm text-destructive">{actionError}</p>}
              </div>
            )}

            {/* Recent Transactions */}
            <div>
              <Label className="text-sm font-semibold"><span key={tUi("recentTransactions")}>{tUi("recentTransactions")}</span></Label>
              <Table className="mt-2">
                <TableHeader>
                  <TableRow>
                    <TableHead><span key={tUi("date")}>{tUi("date")}</span></TableHead>
                    <TableHead><span key={tUi("type")}>{tUi("type")}</span></TableHead>
                    <TableHead className="text-right"><span key={tUi("amount")}>{tUi("amount")}</span></TableHead>
                    <TableHead><span key={tUi("description")}>{tUi("description")}</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(tx.created_at).toLocaleDateString(uiFormattingLocale)}
                      </TableCell>
                      <TableCell>{opTypeBadge(tx.operation_type)}</TableCell>
                      <TableCell className={`text-right font-mono ${tx.amount_microusd < 0 ? 'text-destructive' : 'text-green-600'}`}>
                        {formatCreditsSigned(tx.amount_microusd)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[150px]">
                        <div className="truncate">{tx.description ?? tx.reference_type ?? '—'}</div>
                        {tx.expires_at && (
                          <div className="whitespace-nowrap">{<span key={tUi("expiresValue0", { value0: String(new Date(tx.expires_at).toLocaleString(uiFormattingLocale)) })}>{tUi("expiresValue0", { value0: String(new Date(tx.expires_at).toLocaleString(uiFormattingLocale)) })}</span>}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {transactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                        <span key={tUi("noTransactions")}>{tUi("noTransactions")}</span></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}><span key={tUi("close")}>{tUi("close")}</span></Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
